import { useMemo } from "react";
import { Grid } from "@material-ui/core";
import { KycAddress } from "@/types/kycReview";
import {
  getCityOptions,
  getRegionOptions,
  isPhilippines,
} from "@/components/PRPCM/locationOptions";
import { Errors } from "../state/types";
import { countryItems, SelectItem } from "../options";
import AutocompleteField from "./fields/AutocompleteField";
import TextField from "./fields/TextField";
import ZipField from "./fields/ZipField";

interface Props {
  basePath: string;
  address: KycAddress;
  errors: Errors;
  onField: (path: string, value: string) => void;
}

interface LocationOption {
  name: string;
}

const toItems = (options: LocationOption[]): SelectItem[] =>
  options.map((option) => ({ label: option.name, value: option.name }));

// Country-aware address group, reusing the same PRPCM/locationOptions engine as
// KYC's AddressFields: Philippines uses the local PSGC lists, other countries
// use country-state-city, and regions/cities fall back to free text when no
// options exist. Selecting a country resets the dependent fields so the
// province/city dropdowns always reflect the chosen country.
export default function AddressGroup({
  basePath,
  address,
  errors,
  onField,
}: Props) {
  const country = address.country;
  const isPH = isPhilippines(country);
  const err = (key: string) => errors[`${basePath}.${key}`];
  const set = (key: string, value: string) => onField(`${basePath}.${key}`, value);

  const regionOptions = useMemo(
    () => toItems(getRegionOptions(country) as LocationOption[]),
    [country]
  );
  const cityOptions = useMemo(
    () => toItems(getCityOptions(country, address.province) as LocationOption[]),
    [country, address.province]
  );
  const hasRegionOptions = regionOptions.length > 0;
  const hasCityOptions = cityOptions.length > 0;
  const useCityDropdown = isPH || hasCityOptions;
  const cityDisabled = hasRegionOptions && !address.province;

  // Only fires on user interaction, so prefilled values are never wiped on load.
  const onCountryChange = (value: string) => {
    set("country", value);
    set("province", "");
    set("city", "");
    set("zip", "");
  };
  const onProvinceChange = (value: string) => {
    set("province", value);
    set("city", "");
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <AutocompleteField
          label="Country"
          required
          value={country}
          items={countryItems}
          error={err("country")}
          onChange={onCountryChange}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        {hasRegionOptions ? (
          <AutocompleteField
            label="Province or equivalent"
            required
            value={address.province}
            items={regionOptions}
            error={err("province")}
            onChange={onProvinceChange}
          />
        ) : (
          <TextField
            label="Province or equivalent"
            required
            value={address.province}
            error={err("province")}
            onChange={(v) => set("province", v)}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        {useCityDropdown ? (
          <AutocompleteField
            label="City or municipality"
            required
            disabled={cityDisabled}
            value={address.city}
            items={cityOptions}
            error={err("city")}
            onChange={(v) => set("city", v)}
          />
        ) : (
          <TextField
            label="City or municipality"
            required
            value={address.city}
            error={err("city")}
            onChange={(v) => set("city", v)}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          label="Barangay / Subdivision"
          required={isPH}
          value={address.barangay}
          error={err("barangay")}
          onChange={(v) => set("barangay", v)}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Unit / house no. and street"
          required
          value={address.street}
          error={err("street")}
          onChange={(v) => set("street", v)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <ZipField
          label="ZIP code"
          required={isPH}
          philippines={isPH}
          value={address.zip}
          error={err("zip")}
          onChange={(v) => set("zip", v)}
        />
      </Grid>
    </Grid>
  );
}
